export const lightTheme = {
  colors: {
    primary: '#6366f1',
    primaryHover: '#5855eb',
    secondary: '#10b981',
    secondaryHover: '#059669',
    accent: '#f59e0b',
    danger: '#ef4444',
    dangerHover: '#dc2626',
    warning: '#f59e0b',
    success: '#10b981',
    info: '#3b82f6',

    // Background colors
    background: '#ffffff',
    backgroundSecondary: '#f8fafc',
    backgroundTertiary: '#f1f5f9',

    // Text colors
    textPrimary: '#1e293b',
    textSecondary: '#64748b',
    textMuted: '#94a3b8',
    textInverse: '#ffffff',

    // Border colors
    border: '#e2e8f0',
    borderHover: '#cbd5e1',

    // Card colors
    cardBackground: '#ffffff',
    cardShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    cardShadowHover: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  },

  fonts: {
    primary:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans", "Noto Sans Hebrew", "Noto Sans JP", Arial, sans-serif',
    mono: '"Fira Code", "Monaco", "Consolas", monospace',
  },

  fontSizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
  },

  fontWeights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem',
  },

  borderRadius: {
    sm: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
    full: '9999px',
  },

  breakpoints: {
    xs: '320px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
    '3xl': '1920px',
  },

  // Mobile-first media queries
  media: {
    xs: '(min-width: 320px)',
    sm: '(min-width: 640px)',
    md: '(min-width: 768px)',
    lg: '(min-width: 1024px)',
    xl: '(min-width: 1280px)',
    '2xl': '(min-width: 1536px)',
    '3xl': '(min-width: 1920px)',

    // Max-width queries for mobile-first approach
    maxXs: '(max-width: 319px)',
    maxSm: '(max-width: 639px)',
    maxMd: '(max-width: 767px)',
    maxLg: '(max-width: 1023px)',
    maxXl: '(max-width: 1279px)',
    max2xl: '(max-width: 1535px)',

    // Device-specific queries
    mobile: '(max-width: 767px)',
    tablet: '(min-width: 768px) and (max-width: 1023px)',
    desktop: '(min-width: 1024px)',

    // Orientation queries
    landscape: '(orientation: landscape)',
    portrait: '(orientation: portrait)',

    // Touch device queries
    touch: '(hover: none) and (pointer: coarse)',
    nonTouch: '(hover: hover) and (pointer: fine)',

    // High DPI screens
    retina: '(-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi)',

    // Accessibility queries
    reducedMotion: '(prefers-reduced-motion: reduce)',
    darkMode: '(prefers-color-scheme: dark)',
    lightMode: '(prefers-color-scheme: light)',
    highContrast: '(prefers-contrast: high)',
  },

  // Z-index scale
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    offcanvas: 1050,
    modal: 1055,
    popover: 1070,
    tooltip: 1080,
  },

  transitions: {
    default: 'all 0.15s ease-in-out',
    fast: 'all 0.1s ease-in-out',
    slow: 'all 0.3s ease-in-out',
  },

  // RTL utility functions
  rtl: {
    marginLeft: (value: string) => `margin-inline-start: ${value}`,
    marginRight: (value: string) => `margin-inline-end: ${value}`,
    paddingLeft: (value: string) => `padding-inline-start: ${value}`,
    paddingRight: (value: string) => `padding-inline-end: ${value}`,
    left: (value: string) => `inset-inline-start: ${value}`,
    right: (value: string) => `inset-inline-end: ${value}`,
    textAlign: (direction: 'start' | 'end' | 'center') => `text-align: ${direction}`,
    borderLeft: (value: string) => `border-inline-start: ${value}`,
    borderRight: (value: string) => `border-inline-end: ${value}`,
  },
};

export const darkTheme = {
  ...lightTheme,
  colors: {
    ...lightTheme.colors,
    primary: '#22d3ee',
    primaryHover: '#06b6d4',
    secondary: '#a78bfa',
    secondaryHover: '#8b5cf6',
    accent: '#f472b6',

    // Background colors
    background: '#07070a',
    backgroundSecondary: '#111118',
    backgroundTertiary: '#1f1f2a',

    // Text colors
    textPrimary: '#f5f5f7',
    textSecondary: '#a1a1aa',
    textMuted: '#71717a',
    textInverse: '#07070a',

    // Border colors
    border: '#1f1f2a',
    borderHover: '#2a2a38',

    // Card colors
    cardBackground: '#111118',
    cardShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px 0 rgba(0, 0, 0, 0.3)',
    cardShadowHover: '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
  },
};

export type Theme = typeof lightTheme;
